import React, { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { ArrowLeft, Check, ChevronDown, ChevronUp, Copy, KeyRound, QrCode, RotateCcw, Shield, ShieldCheck } from "lucide-react-native";
import { api } from "../api/client";
import { Badge, Button, Card, Field, PasswordInput, ScreenHeader } from "../components/UI";
import { colors, styles } from "../styles";

export function ProfileScreen({ user }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  // MFA Reconfiguration State
  const [reconfigStep, setReconfigStep] = useState("idle"); // "idle" | "password_prompt" | "qr_verify"
  const [reconfigPassword, setReconfigPassword] = useState("");
  const [reconfigLoading, setReconfigLoading] = useState(false);
  const [reconfigMfaData, setReconfigMfaData] = useState(null);
  const [reconfigCode, setReconfigCode] = useState("");
  const [reconfigError, setReconfigError] = useState("");
  const [copiedKey, setCopiedKey] = useState(false);
  const [showManualKey, setShowManualKey] = useState(false);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await api.changePassword(currentPassword, password);
      setCurrentPassword("");
      setPassword("");
      setMessage({ type: "success", text: "Password changed successfully." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleStartReconfig = async () => {
    setReconfigError("");
    if (!reconfigPassword) {
      setReconfigError("Please enter your current password to continue.");
      return;
    }
    setReconfigLoading(true);
    try {
      const data = await api.reconfigureMfaInit(reconfigPassword);
      setReconfigMfaData(data);
      setReconfigPassword("");
      setReconfigCode("");
      setReconfigStep("qr_verify");
    } catch (err) {
      setReconfigError(err.message || "Failed to initialize MFA reconfiguration.");
    } finally {
      setReconfigLoading(false);
    }
  };

  const handleConfirmReconfig = async () => {
    setReconfigError("");
    if (!reconfigCode || reconfigCode.trim().length < 6) {
      setReconfigError("Please enter the 6-digit code from your new authenticator app.");
      return;
    }
    setReconfigLoading(true);
    try {
      const res = await api.reconfigureMfaConfirm(reconfigCode.trim());
      setMessage({ type: "success", text: res.message || "MFA reconfigured successfully." });
      setReconfigStep("idle");
      setReconfigMfaData(null);
      setReconfigCode("");
    } catch (err) {
      setReconfigError(err.message || "Verification failed. Check the code and try again.");
    } finally {
      setReconfigLoading(false);
    }
  };

  const copySecretKey = () => {
    if (reconfigMfaData?.secret && typeof navigator !== "undefined" && navigator.clipboard) {
      navigator.clipboard.writeText(reconfigMfaData.secret);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 2000);
    }
  };

  return (
    <View style={styles.page}>
      <ScreenHeader
        title="My Profile & Security"
        subtitle={`${user?.name || user?.full_name || user?.username} | ${String(user?.role || "sales").toUpperCase()}`}
      />

      {message ? (
        <Text style={[styles.message, message.type === "error" ? styles.errorMessage : styles.successMessage]}>
          {message.text}
        </Text>
      ) : null}

      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
        {/* Two-Factor Authentication (MFA) Card */}
        <Card style={{ flex: 1, minWidth: 320, maxWidth: 540 }}>
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
            <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
              <ShieldCheck size={24} color={colors.primary} />
              <Text style={styles.h2}>Two-Factor Authentication (MFA)</Text>
            </View>
            <Badge variant="success">ENFORCED (ACTIVE)</Badge>
          </View>

          <Text style={[styles.small, { color: colors.muted, lineHeight: 18, marginTop: 4 }]}>
            Multi-Factor Authentication is enforced across all accounts. Every sign-in requires your password plus a 6-digit TOTP code generated by Google Authenticator, Microsoft Authenticator, or Authy.
          </Text>

          {reconfigError ? (
            <Text style={[styles.message, styles.errorMessage]}>{reconfigError}</Text>
          ) : null}

          {reconfigStep === "idle" && (
            <View style={{ marginTop: 12 }}>
              <Button
                variant="secondary"
                icon={RotateCcw}
                onPress={() => {
                  setReconfigError("");
                  setReconfigPassword("");
                  setReconfigStep("password_prompt");
                }}
              >
                Reconfigure Authenticator App (New Phone)
              </Button>
            </View>
          )}

          {reconfigStep === "password_prompt" && (
            <View style={{ marginTop: 12, gap: 14 }}>
              <Text style={[styles.small, { fontWeight: "600", color: colors.text }]}>
                Confirm your current password to link a new authenticator device:
              </Text>
              <PasswordInput
                label="Current Password *"
                value={reconfigPassword}
                onChangeText={setReconfigPassword}
                placeholder="Enter your current password"
              />
              <View style={{ flexDirection: "row", gap: 10 }}>
                <Button
                  icon={QrCode}
                  onPress={handleStartReconfig}
                  disabled={reconfigLoading || !reconfigPassword}
                  loading={reconfigLoading}
                >
                  Continue to QR Code
                </Button>
                <Button
                  variant="secondary"
                  onPress={() => {
                    setReconfigStep("idle");
                    setReconfigError("");
                  }}
                >
                  Cancel
                </Button>
              </View>
            </View>
          )}

          {reconfigStep === "qr_verify" && reconfigMfaData && (
            <View style={{ marginTop: 12, gap: 14 }}>
              <Text style={[styles.small, { color: colors.muted, lineHeight: 18 }]}>
                Scan this new QR code in your authenticator app, then enter the 6-digit code below to confirm:
              </Text>

              <View style={{ alignItems: "center", marginVertical: 6 }}>
                <div style={{
                  padding: 10,
                  backgroundColor: "#FFFFFF",
                  borderRadius: 12,
                  border: `1px solid ${colors.border}`,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  display: "inline-flex"
                }}>
                  <img
                    src={reconfigMfaData.qrCode}
                    alt="New Authenticator QR Code"
                    style={{ width: 170, height: 170, display: "block" }}
                  />
                </div>
              </View>

              <View style={{ backgroundColor: colors.secondaryBg, borderRadius: 8, padding: 10, borderWidth: 1, borderColor: colors.border }}>
                <Pressable
                  onPress={() => setShowManualKey(!showManualKey)}
                  style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center" }}
                >
                  <Text style={[styles.small, { fontWeight: "600", color: colors.text }]}>Manual Setup Key</Text>
                  {showManualKey ? <ChevronUp size={16} color={colors.muted} /> : <ChevronDown size={16} color={colors.muted} />}
                </Pressable>

                {showManualKey ? (
                  <View style={{ marginTop: 8, gap: 6 }}>
                    <Text style={[styles.small, { fontFamily: "monospace", fontSize: 12, backgroundColor: "#FFFFFF", padding: 8, borderRadius: 6, borderWidth: 1, borderColor: colors.border, textAlign: "center", userSelect: "all" }]}>
                      {reconfigMfaData.secret}
                    </Text>
                    <Pressable
                      onPress={copySecretKey}
                      style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 6 }}
                    >
                      {copiedKey ? <Check size={14} color="#16a34a" /> : <Copy size={14} color={colors.primary} />}
                      <Text style={[styles.small, { color: copiedKey ? "#16a34a" : colors.primary, fontWeight: "600" }]}>
                        {copiedKey ? "Copied to clipboard!" : "Copy Key"}
                      </Text>
                    </Pressable>
                  </View>
                ) : null}
              </View>

              <Field
                label="6-Digit Verification Code from New App *"
                value={reconfigCode}
                onChangeText={setReconfigCode}
                placeholder="e.g. 123456"
                maxLength={6}
                keyboardType="numeric"
              />

              <View style={{ flexDirection: "row", gap: 10 }}>
                <Button
                  icon={ShieldCheck}
                  onPress={handleConfirmReconfig}
                  disabled={reconfigLoading || reconfigCode.trim().length !== 6}
                  loading={reconfigLoading}
                >
                  Confirm Reconfiguration
                </Button>
                <Button
                  variant="secondary"
                  icon={ArrowLeft}
                  onPress={() => {
                    setReconfigStep("idle");
                    setReconfigMfaData(null);
                    setReconfigError("");
                  }}
                >
                  Cancel
                </Button>
              </View>
            </View>
          )}
        </Card>

        {/* Change Password Card */}
        <Card style={{ flex: 1, minWidth: 320, maxWidth: 540 }}>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 }}>
            <Shield size={24} color={colors.primary} />
            <Text style={styles.h2}>Change Password</Text>
          </View>

          <View style={{ gap: 16 }}>
            <PasswordInput
              label="Current Password"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              placeholder="Enter your current password"
            />
            <PasswordInput
              label="New Password"
              value={password}
              onChangeText={setPassword}
              placeholder="Enter new strong password"
            />
          </View>

          <Button icon={KeyRound} onPress={save} disabled={saving} loading={saving} style={{ marginTop: 8 }}>
            {saving ? "Updating Password..." : "Update Password"}
          </Button>
        </Card>
      </View>
    </View>
  );
}
